import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Grid,
  Paper,
  Stack,
  Tab,
  Tabs,
  Toolbar,
  Typography,
} from '@mui/material';
import { Layout, PermissionWrapper, PopupConfirm } from 'components';
import PopupEditBaseRAC from 'components/Popups/AC/PopupEditBaseRAC';
import { drawerWidth } from 'config';
import { NotifyService, convertDobToAge, convertTimeToLocal } from 'helpers';
import { useDateTime } from 'hooks';
import { POPUP_ACTION, TYPE_POPUP } from 'models';
import {
  ETAB_NUMBER_SUB_DETAIL,
  ETAB_TEXT_SUB_DETAIL,
  AC_BASE_REQUEST_STATUS,
} from 'models/subscription';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { GET_PARENT_DETAIL_REQUEST } from 'store/reducers/accounts/actionTypes';
import { checkCanCreateTrialJob } from 'store/reducers/jobs/actionTypes';
import { setGlobalLoading } from 'store/reducers/global';
import { updateTabActive } from 'store/reducers/subscription';
import { getBaseRequestDetailAction } from 'store/reducers/subscription/actionType';
import { skipTrialApi } from 'services/subscription';
import { statusLabel } from '../data';
import ACJobList from './Jobs';
import PurchaseHistory from './PurchaseHistory';
import Sitters from './Sitters';
import { subDetailTab } from './data';
import { DetailItem } from './styles';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`nanny-detail-tabpanel-${index}`}
      aria-labelledby={`nanny-detail-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `nanny-detail-tab-${index}`,
    'aria-controls': `nanny-detail-tabpanel-${index}`,
  };
}

export const SubscriptionDetail = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const { formatDateTime } = useDateTime();

  const [popupAction, setPopupAction] = useState<POPUP_ACTION>();

  const [openConfirmSkipTrial, setOpenConfirmSkipTrial] = useState(false);

  const { parent_detail } = useAppSelector(
    (state) => state.accountReducer.parents
  );
  const { base_request_detail, tabActive } = useAppSelector(
    (state) => state.subscriptionReducer.subscription
  );

  const handleChangeTabValue = (_: React.SyntheticEvent, newValue: number) => {
    dispatch(updateTabActive(newValue));

    const mappingTab =
      newValue === 0
        ? ETAB_TEXT_SUB_DETAIL.SITTERS
        : newValue === 1
        ? ETAB_TEXT_SUB_DETAIL.JOBS
        : ETAB_TEXT_SUB_DETAIL.PURCHASE_HISTORY;

    // Update URL with the tab parameter
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('tab', mappingTab);
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  const onShowPopupAction = (type: TYPE_POPUP) => {
    setPopupAction({ type: type });
  };

  const onClosePopupAction = () => {
    setPopupAction(undefined);
  };

  const onSkipTrial = async () => {
    if (!base_request_detail?.id) return;
    try {
      dispatch(setGlobalLoading(true));
      const res = await skipTrialApi(base_request_detail.id);
      NotifyService.success(res);
      setOpenConfirmSkipTrial(false);
      onReloadDetail();
    } catch (error) {
      NotifyService.error(error);
    } finally {
      dispatch(setGlobalLoading(false));
    }
  };

  const displayAddress = () => {
    const { street, unit_number, block_number, postal_code } =
      base_request_detail?.address || {};
    return `${unit_number}, ${block_number} ${street}, Singapore ${postal_code}`;
  };

  const onReloadDetail = () => {
    dispatch({
      type: GET_PARENT_DETAIL_REQUEST,
      payload: { id },
    });
    dispatch(getBaseRequestDetailAction({ data: { id } }));
    dispatch(checkCanCreateTrialJob({ parent_id: id }));
  };

  useEffect(() => {
    onReloadDetail();
    const currentTab = searchParams.get('tab');
    if (currentTab) {
      const mappingTab =
        currentTab === ETAB_TEXT_SUB_DETAIL.SITTERS
          ? 0
          : currentTab === ETAB_TEXT_SUB_DETAIL.JOBS
          ? 1
          : 2;
      dispatch(updateTabActive(mappingTab));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Layout>
      <AppBar
        position="fixed"
        sx={{
          width: `calc(100% - ${drawerWidth}px)`,
          ml: `${drawerWidth}px`,
          backgroundColor: 'white.light',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        }}
        elevation={0}
      >
        <Toolbar
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Grid>
            <Typography
              sx={{
                fontSize: '20px',
                lineHeight: 1,
                color: 'grey1.light',
              }}
            >
              Concierge / Subscription /{base_request_detail?.number}
            </Typography>
          </Grid>
        </Toolbar>
      </AppBar>
      <Paper elevation={0} sx={{ p: '16px' }}>
        <Grid container justifyContent="space-between" pb="16px">
          <Button
            startIcon={<ArrowBackIosNewIcon />}
            sx={{
              p: 0,
              color: 'black.light',
              ':hover': {
                backgroundColor: 'white.light',
              },
            }}
            variant="text"
            onClick={() => {
              navigate(`/concierge/subscription`);
              dispatch(updateTabActive(0));
            }}
          >
            Back
          </Button>
        </Grid>
        <Stack spacing={2}>
          <Box
            sx={{
              backgroundColor: 'rgba(34, 111, 227, 0.04)',
              padding: '21px 31px',
            }}
          >
            <Grid container rowSpacing={'8px'} columnSpacing={'50px'}>
              <DetailItem item>
                <Stack spacing={2} direction="row" alignItems="center">
                  <Avatar
                    src={parent_detail?.profile_picture || ''}
                    sx={{
                      width: '40px',
                      height: '40px',
                      marginRight: '6px',
                    }}
                  />
                  <Stack spacing={0.5}>
                    <Typography
                      fontWeight={500}
                      lineHeight="14px"
                      fontSize={14}
                      letterSpacing="0.1px"
                      color="text.secondary"
                    >
                      Parent Name (First+Last)
                    </Typography>
                    <Typography
                      fontWeight={400}
                      lineHeight="16px"
                      fontSize={14}
                      letterSpacing="0.25px"
                      color="black.light"
                      textTransform="capitalize"
                    >
                      {(parent_detail?.first_name &&
                        `${parent_detail?.first_name} ${parent_detail?.last_name}`) ||
                        'No data to display yet'}
                    </Typography>
                  </Stack>
                </Stack>
              </DetailItem>
              <DetailItem item>
                <Typography
                  fontWeight={500}
                  lineHeight="14px"
                  fontSize={14}
                  letterSpacing="0.1px"
                  color="text.secondary"
                >
                  Email
                </Typography>
                <Typography
                  fontWeight={400}
                  lineHeight="16px"
                  fontSize={14}
                  letterSpacing="0.25px"
                  color="black.light"
                >
                  {parent_detail?.email}
                </Typography>
              </DetailItem>
              <DetailItem item>
                <Typography
                  fontWeight={500}
                  lineHeight="14px"
                  fontSize={14}
                  letterSpacing="0.1px"
                  color="text.secondary"
                >
                  Phone Number
                </Typography>
                <Typography
                  fontWeight={400}
                  lineHeight="16px"
                  fontSize={14}
                  letterSpacing="0.25px"
                  color="black.light"
                >
                  {parent_detail?.phone_number}
                </Typography>
              </DetailItem>
              <DetailItem item>
                <Typography
                  fontWeight={500}
                  lineHeight="14px"
                  fontSize={14}
                  letterSpacing="0.1px"
                  color="text.secondary"
                >
                  Balance
                </Typography>
                <Typography
                  fontWeight={400}
                  lineHeight="16px"
                  fontSize={14}
                  letterSpacing="0.25px"
                  color="black.light"
                >
                  {parent_detail?.ac_balance
                    ? `$${Number(parent_detail?.ac_balance).toFixed(2)}`
                    : 'No data to display yet'}
                </Typography>
              </DetailItem>
              <DetailItem item>
                <Typography
                  fontWeight={500}
                  lineHeight="14px"
                  fontSize={14}
                  letterSpacing="0.1px"
                  color="text.secondary"
                >
                  Sitter Rate
                </Typography>
                <Typography
                  fontWeight={400}
                  lineHeight="16px"
                  fontSize={14}
                  letterSpacing="0.25px"
                  color="black.light"
                >
                  {`$${base_request_detail?.sitter_rate?.toFixed(2)}` ||
                    'No data to display yet'}
                </Typography>
              </DetailItem>
            </Grid>
          </Box>
          <Box
            sx={{
              backgroundColor: 'rgba(34, 111, 227, 0.04)',
              padding: '21px 31px',
            }}
          >
            <Stack direction="row" justifyContent="space-between">
              <Box>
                <Grid
                  container
                  rowSpacing={'8px'}
                  columnSpacing={'70px'}
                  mb={3}
                >
                  <DetailItem item>
                    <Typography
                      fontWeight={500}
                      lineHeight="14px"
                      fontSize={14}
                      letterSpacing="0.1px"
                      color="text.secondary"
                    >
                      Subscription ID
                    </Typography>
                    <Typography
                      fontWeight={400}
                      lineHeight="16px"
                      fontSize={14}
                      letterSpacing="0.25px"
                      color="black.light"
                    >
                      {base_request_detail?.number || 'No data to display yet'}
                    </Typography>
                  </DetailItem>
                  <DetailItem item>
                    <Typography
                      fontWeight={500}
                      lineHeight="14px"
                      fontSize={14}
                      letterSpacing="0.1px"
                      color="text.secondary"
                    >
                      Booking Status
                    </Typography>
                    <Typography
                      fontWeight={400}
                      lineHeight="16px"
                      fontSize={14}
                      letterSpacing="0.25px"
                      color="black.light"
                      textTransform="capitalize"
                    >
                      {base_request_detail?.status
                        ? statusLabel[base_request_detail.status]
                        : 'No data to display yet'}
                    </Typography>
                  </DetailItem>
                  <DetailItem item>
                    <Typography
                      fontWeight={500}
                      lineHeight="14px"
                      fontSize={14}
                      letterSpacing="0.1px"
                      color="text.secondary"
                    >
                      From
                    </Typography>
                    <Typography
                      fontWeight={400}
                      lineHeight="16px"
                      fontSize={14}
                      letterSpacing="0.25px"
                      color="black.light"
                    >
                      {(base_request_detail?.start_datetime &&
                        formatDateTime(
                          convertTimeToLocal(
                            base_request_detail?.start_datetime
                          )
                        )) ||
                        'No data to display yet'}
                    </Typography>
                  </DetailItem>
                  <DetailItem item>
                    <Typography
                      fontWeight={500}
                      lineHeight="14px"
                      fontSize={14}
                      letterSpacing="0.1px"
                      color="text.secondary"
                    >
                      To
                    </Typography>
                    <Typography
                      fontWeight={400}
                      lineHeight="16px"
                      fontSize={14}
                      letterSpacing="0.25px"
                      color="black.light"
                    >
                      {base_request_detail?.end_datetime &&
                        formatDateTime(
                          convertTimeToLocal(base_request_detail?.end_datetime)
                        )}
                    </Typography>
                  </DetailItem>
                  <DetailItem item>
                    <Typography
                      fontWeight={500}
                      lineHeight="14px"
                      fontSize={14}
                      letterSpacing="0.1px"
                      color="text.secondary"
                    >
                      Requested on
                    </Typography>
                    <Typography
                      fontWeight={400}
                      lineHeight="16px"
                      fontSize={14}
                      letterSpacing="0.25px"
                      color="black.light"
                    >
                      {formatDateTime(base_request_detail?.createdAt) ||
                        'No data to display yet'}
                    </Typography>
                  </DetailItem>
                </Grid>
                <Grid container rowSpacing={'8px'} columnSpacing={'70px'}>
                  <DetailItem item>
                    <Typography
                      fontWeight={500}
                      lineHeight="14px"
                      fontSize={14}
                      letterSpacing="0.1px"
                      color="text.secondary"
                    >
                      Address
                    </Typography>
                    <Typography
                      fontWeight={400}
                      lineHeight="16px"
                      fontSize={14}
                      letterSpacing="0.25px"
                      color="black.light"
                    >
                      {displayAddress()}
                    </Typography>
                  </DetailItem>
                  <DetailItem item>
                    <Typography
                      fontWeight={500}
                      lineHeight="14px"
                      fontSize={14}
                      letterSpacing="0.1px"
                      color="text.secondary"
                    >
                      Children
                    </Typography>
                    <Typography
                      fontWeight={400}
                      lineHeight="16px"
                      fontSize={14}
                      letterSpacing="0.25px"
                      color="black.light"
                      textTransform="capitalize"
                    >
                      {base_request_detail?.children
                        .map(
                          (item) =>
                            `${item.full_name} (${convertDobToAge(
                              item.date_of_birth
                            )})`
                        )
                        .join(', ') || 'No data to display yet'}
                    </Typography>
                  </DetailItem>
                  <DetailItem item>
                    <Typography
                      fontWeight={500}
                      lineHeight="14px"
                      fontSize={14}
                      letterSpacing="0.1px"
                      color="text.secondary"
                    >
                      Pets
                    </Typography>
                    <Typography
                      fontWeight={400}
                      lineHeight="16px"
                      fontSize={14}
                      letterSpacing="0.25px"
                      color="black.light"
                      textTransform="capitalize"
                    >
                      {base_request_detail?.pets.cats &&
                      base_request_detail?.pets.dogs
                        ? 'Cats, Dogs'
                        : (base_request_detail?.pets.cats && 'Cats') ||
                          (base_request_detail?.pets.dogs && 'Dogs') ||
                          'No data to display yet'}
                    </Typography>
                  </DetailItem>
                </Grid>
              </Box>
              <PermissionWrapper allowedRoles={['superadmin', 'staff']}>
                <Stack direction="column" spacing="15px">
                  <Button
                    sx={{
                      height: '14px',
                      fontSize: '14px',
                      whiteSpace: 'nowrap',
                    }}
                    variant="contained"
                    color="secondary"
                    onClick={() => onShowPopupAction(TYPE_POPUP.EDIT)}
                  >
                    Edit Request
                  </Button>
                  {[
                    AC_BASE_REQUEST_STATUS.PENDING_PAYMENT,
                    AC_BASE_REQUEST_STATUS.PAYMENT_FAILED,
                    AC_BASE_REQUEST_STATUS.SEARCH_REQUESTED,
                    AC_BASE_REQUEST_STATUS.SEARCH_FOUND,
                    AC_BASE_REQUEST_STATUS.PARENT_ACCEPTED,
                    AC_BASE_REQUEST_STATUS.PARENT_REJECTED,
                    AC_BASE_REQUEST_STATUS.TRIAL_CREATED,
                    AC_BASE_REQUEST_STATUS.TRIAL_PAID,
                  ].includes(
                    base_request_detail?.status as AC_BASE_REQUEST_STATUS
                  ) && (
                    <Button
                      sx={{
                        height: '14px',
                        fontSize: '14px',
                        whiteSpace: 'nowrap',
                      }}
                      variant="contained"
                      color="primary"
                      onClick={() => setOpenConfirmSkipTrial(true)}
                    >
                      Skip trial
                    </Button>
                  )}
                </Stack>
              </PermissionWrapper>
            </Stack>
          </Box>
        </Stack>
        {openConfirmSkipTrial && (
          <PopupConfirm
            open={openConfirmSkipTrial}
            title="Confirm Skip Trial"
            description="Are you sure you want to skip trial for this subscription?"
            onClose={() => setOpenConfirmSkipTrial(false)}
            onConfirm={onSkipTrial}
          />
        )}
        <Grid
          container
          sx={{
            borderBottom: '1px solid  rgba(0, 0, 0, 0.08)',
            marginBottom: '16px',
          }}
        >
          <Tabs value={tabActive} onChange={handleChangeTabValue}>
            {subDetailTab.map((item, index) => {
              return (
                <Tab
                  key={index}
                  label={item.label}
                  sx={{
                    fontSize: '16px',
                    fontWeight: 400,
                    lineHeight: '125%',
                    letterSpacing: '0.1px',
                    color: '#959595',
                    textTransform: 'capitalize',
                  }}
                  {...a11yProps(index)}
                />
              );
            })}
          </Tabs>
        </Grid>
        <TabPanel value={tabActive} index={ETAB_NUMBER_SUB_DETAIL.SITTERS}>
          <Sitters
            sitter_rate={base_request_detail?.sitter_rate || 0}
            requests_detail={base_request_detail}
            onSuccess={onReloadDetail}
          />
        </TabPanel>
        <TabPanel value={tabActive} index={ETAB_NUMBER_SUB_DETAIL.JOBS}>
          <ACJobList
            parentId={id}
            dataCreateAC={{
              sitterRate: base_request_detail?.sitter_rate || 0,
              baseRequest: base_request_detail,
            }}
          />
        </TabPanel>
        <TabPanel
          value={tabActive}
          index={ETAB_NUMBER_SUB_DETAIL.PURCHASE_HISTORY}
        >
          <PurchaseHistory parentId={id ?? ''} />
        </TabPanel>
      </Paper>
      {popupAction?.type === TYPE_POPUP.EDIT && (
        <PopupEditBaseRAC
          parentId={id}
          open={popupAction?.type === TYPE_POPUP.EDIT}
          subject={base_request_detail?.id}
          handleClosePopup={() => onClosePopupAction()}
          onSuccess={onReloadDetail}
        />
      )}
    </Layout>
  );
};

export default SubscriptionDetail;
